
import { useEffect, useRef, useState } from 'react';

export default function Pricing() {
  const [isVisible, setIsVisible] = useState(false);
  const [animated, setAnimated] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          setAnimated(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleJoinWaitlist = () => {
    scrollToSection('final-cta');
  };

  const tiers = [
    {
      name: 'FOUNDER',
      badge: '🏆 500 spots',
      price: '$25',
      period: '/month',
      billing: 'Billed $300/year',
      features: [
        'Unlimited pleadings and contracts',
        'Voice dictation from anywhere',
        'Court-specific templates',
        'Local jurisdictions',
        'Founder badge',
        'Rate locked forever',
      ],
      cta: 'Claim Founder Spot',
      highlight: true,
      limited: true,
    },
    {
      name: 'PRE-LAUNCH',
      badge: 'Available until public launch',
      price: '$79',
      period: '/month',
      billing: 'Cancel anytime',
      features: [
        'Unlimited pleadings and contracts',
        'Voice dictation from anywhere',
        'Court-specific templates',
        'Local jurisdictions',
      ],
      cta: 'Join Pre-Launch',
      highlight: false,
      limited: false,
    },
    {
      name: 'STANDARD',
      badge: 'Coming at public launch',
      price: '$149',
      period: '/month',
      billing: 'Cancel anytime',
      features: [
        'Unlimited pleadings and contracts',
        'Voice dictation from anywhere',
        'Court-specific templates',
        'Local jurisdictions',
      ],
      cta: 'Join Waitlist',
      highlight: false,
      limited: false,
    },
  ];

  return (
    <section id="pricing" ref={sectionRef} className="relative py-20 sm:py-32 bg-white">
      <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
        <div className="text-center mb-12 sm:mb-16">
          <h2
            className={`text-[#09090b] text-[32px] sm:text-[44px] lg:text-[56px] font-bold leading-tight mb-4 transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            Founder Pricing
          </h2>
          <p
            className={`text-[#52525b] text-[16px] sm:text-[20px] transition-all duration-700 delay-200 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            First 500 lock in rates permanently
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {tiers.map((tier, index) => (
            <div
              key={index}
              className={`relative p-8 rounded-2xl transition-all duration-700 ${
                tier.highlight
                  ? 'bg-gradient-to-b from-[#d4af37]/10 to-white border-2 border-[#d4af37] shadow-[0_0_60px_rgba(212,175,55,0.2)]'
                  : 'bg-[#f5f0e8] border border-[#e8dcc8]'
              } ${animated ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              {tier.limited && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-2 bg-[#d4af37] text-black text-sm font-black rounded-full whitespace-nowrap">
                  LIMITED
                </div>
              )}

              <div className="space-y-6">
                <div>
                  <h3 className={`text-3xl font-black mb-2 ${tier.highlight ? 'text-[#d4af37]' : 'text-[#09090b]'}`}>
                    {tier.name}
                  </h3>
                  <p className="text-[#71717a] text-sm font-semibold">{tier.badge}</p>
                </div>

                <div>
                  <div className="flex items-baseline gap-2">
                    <span className={`text-6xl font-black ${tier.highlight ? 'text-[#d4af37]' : 'text-[#09090b]'}`}>
                      {tier.price}
                    </span>
                    <span className="text-[#71717a] text-xl">{tier.period}</span>
                  </div>
                  <p className="text-[#71717a] text-sm mt-2">{tier.billing}</p>
                </div>

                <div className="space-y-3 py-6 border-t border-b border-[#e4e4e7]">
                  {tier.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <span className={`text-lg mt-0.5 ${tier.highlight ? 'text-[#d4af37]' : 'text-[#8B4513]'}`}>✓</span>
                      <span className="text-[#52525b] text-base">{feature}</span>
                    </div>
                  ))}
                </div>

                <button
                  onClick={handleJoinWaitlist}
                  className={`w-full px-6 py-4 rounded-full font-bold text-base transition-all cursor-pointer whitespace-nowrap ${
                    tier.highlight
                      ? 'bg-[#d4af37] text-black hover:shadow-[0_0_40px_rgba(212,175,55,0.5)]'
                      : 'bg-[#8B4513] text-white hover:shadow-[0_0_40px_rgba(139,69,19,0.3)]'
                  }`}
                >
                  {tier.cta}
                </button>
              </div>
            </div>
          ))}
        </div>

        <p
          className={`text-[#71717a] text-[14px] sm:text-[16px] text-center mt-10 sm:mt-12 transition-all duration-700 delay-400 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          *Ends at launch. Standard: $149/mo
        </p>

        <div
          className={`bg-[#f5f0e8] border border-[#e8dcc8] rounded-2xl p-8 max-w-[800px] mx-auto mt-10 transition-all duration-700 delay-600 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#e4e4e7]">
                  <th className="pb-4 text-[#71717a] text-[14px] font-semibold"></th>
                  <th className="pb-4 text-[#09090b] text-[16px] font-bold">HushLegal</th>
                  <th className="pb-4 text-[#71717a] text-[16px] font-bold">Others</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-[#e4e4e7]">
                  <td className="py-4 text-[#52525b] text-[15px]">Price</td>
                  <td className="py-4 text-[#09090b] text-[15px] font-semibold">$25-79</td>
                  <td className="py-4 text-[#71717a] text-[15px]">$300+</td>
                </tr>
                <tr className="border-b border-[#e4e4e7]">
                  <td className="py-4 text-[#52525b] text-[15px]">Mobile/Voice</td>
                  <td className="py-4 text-[#8B4513] text-[18px]">✓</td>
                  <td className="py-4 text-[#71717a] text-[18px]">✗</td>
                </tr>
                <tr>
                  <td className="py-4 text-[#52525b] text-[15px]">Zero Storage</td>
                  <td className="py-4 text-[#8B4513] text-[18px]">✓</td>
                  <td className="py-4 text-[#71717a] text-[18px]">✗</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
}
